import React from 'react'
import { Navigate } from 'react-router-dom'

import { FeatureFlag, useFeatureFlag, useFeatureFlagsReadyFor } from 'utils/featureFlags'
import { useFlagUserId } from 'utils/featureFlags/useFlagUserId'
import LoadingPage from 'components/pages/General/LoadingPage/LoadingPage'

import { flagOffFallbackPath } from './embedFallbackPath'
import { usePfEmbed } from './usePfEmbed'

/**
 * Renders PF v2 only once `ALLOW_PF_REVAMP` is final for the signed-in user.
 *
 * LaunchDarkly first evaluates the anonymous context and identifies the user
 * later, so a per-user rollout reads `false` for a moment on every cold load —
 * acting on it would replace a deep link, a reload or a Stripe return with the
 * legacy flow. The identity is the one `App.controller` identifies with.
 */
export const PfRevampFlagGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const flagsReady = useFeatureFlagsReadyFor(useFlagUserId())
  const allowPfRevamp = useFeatureFlag(FeatureFlag.ALLOW_PF_REVAMP)
  const { isEmbed } = usePfEmbed()

  if (!flagsReady) return <LoadingPage />
  // Inside the iframe this must stay on the embed path — RouteSwitch renders
  // legacy there — or a flag disagreement navigates the frame out of the CRM.
  if (!allowPfRevamp) return <Navigate to={flagOffFallbackPath(isEmbed)} replace />

  return <>{children}</>
}
